import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { LuChevronDown, LuChevronUp } from 'react-icons/lu'
import { Box, Text } from '@chakra-ui/react'

import { useClickOutside } from 'utils/useClickOutside'

import { ErrorMessage } from '../ErrorMessage/styles'

import { Button, Icon, Option, OptionRadio, Options, Placeholder, SelectWrapper } from './styles'
import { SelectOption, useSelect } from './useSelect'

export interface SelectProps {
  name: string
  options: SelectOption[]
  defaultOption?: SelectOption | null
  placeholder?: string
  error?: any
  disabled?: boolean
  onChange?: (value: string) => void
  icon?: string
  touched?: boolean
  onClear?: () => void
  shouldHideClearButton?: boolean
  className?: string
}

export interface RenderInputProps {
  chosenOption: any
  isOpen: boolean
  isInvalid: boolean
  disabled?: boolean
  onToggle: () => void
  onClear: (e: any) => void
}

export const Select: React.FC<SelectProps> = ({
  options,
  defaultOption,
  placeholder,
  error,
  disabled,
  onChange,
  icon,
  touched,
  onClear,
  shouldHideClearButton,
  className,
}) => {
  const { isSelectOpen, toggleSelect, chosenOption, chooseOption, clearOption, closeSelect } = useSelect(
    onChange,
    defaultOption,
  )
  const wrapperRef = useRef<HTMLDivElement>(null!)
  const { t } = useTranslation('common')

  const handleItemClick = (child: { props: any }) => () => {
    chooseOption(child.props)
  }

  useClickOutside(wrapperRef, () => closeSelect())

  return (
    <SelectWrapper ref={wrapperRef} isClickable>
      <div>
        <span>
          <Button
            disabled={disabled}
            isOpen={isSelectOpen}
            onClick={toggleSelect}
            type="button"
            isInvalid={touched && !!error}
            className={className}
          >
            {icon && <Icon src={icon} alt="" />}
            <Placeholder className="gtm-select-selected-option" isIcon={!!icon}>
              {t(chosenOption?.label || '') || placeholder}
            </Placeholder>
            {!shouldHideClearButton && chosenOption ? (
              <img
                src="/icons/close.svg"
                alt="Clear"
                onClick={(e) => {
                  e.stopPropagation()
                  toggleSelect()
                  clearOption()
                  onClear && onClear()
                }}
              />
            ) : (
              <Box color="green.a250">{isSelectOpen ? <LuChevronUp size={24} /> : <LuChevronDown size={24} />}</Box>
            )}
          </Button>
        </span>
        {isSelectOpen && (
          <Options>
            {options.map((option, index) => {
              return (
                <Option key={index} onClick={handleItemClick({ props: option })} className="gtm-select-option">
                  <OptionRadio isChecked={chosenOption?.label === option.label}>
                    {chosenOption?.label === option.label ? <img src="/icons/tick.svg" alt="" /> : null}
                  </OptionRadio>
                  <Box>
                    {t(option.label)}
                    {option.subLabel && (
                      <Text color="green.a250" fontSize="xs">
                        {option.subLabel}
                      </Text>
                    )}
                  </Box>
                </Option>
              )
            })}
          </Options>
        )}
      </div>
      <ErrorMessage>{touched && error}</ErrorMessage>
    </SelectWrapper>
  )
}
